import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { fmtShowDate, TicketButton, CalendarButtons, Poster } from "@/components/shows/showsUtils";

const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

const share = async () => {
  const url = window.location.href;
  try {
    if (navigator.share) await navigator.share({ title: "Spettacoli Areté Teatro", url });
    else { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
  } catch { /* condivisione annullata */ }
};

const PublicEvent = ({ ev }) => (
  <Card className="p-5 bg-card border-border space-y-3" data-testid={`public-show-${ev.id}`}>
    <Poster key={ev.poster_url} url={ev.poster_url} title={ev.title} testid={`public-poster-${ev.id}`} />
    <h2 className="text-2xl font-semibold break-words">{ev.title}</h2>
    {ev.description && <p className="whitespace-pre-line break-words">{ev.description}</p>}
    {ev.location && <p className="text-muted-foreground flex items-start gap-1.5 break-words"><MapPin size={16} className="mt-1 shrink-0" /> {ev.location}</p>}
    <ul className="space-y-4 pt-1">
      {ev.performances.map((p) => (
        <li key={p.id} className="space-y-2" data-testid={`public-perf-${p.id}`}>
          <p className="text-xl font-bold first-letter:uppercase">{fmtShowDate(p.date, p.time)}</p>
          <TicketButton href={p.link} testid={`public-tickets-${p.id}`} />
          <CalendarButtons ics={p.ics} testid={`public-cal-${p.id}`} title={ev.title} date={p.date} time={p.time} location={ev.location} details={ev.description} />
        </li>
      ))}
    </ul>
  </Card>
);

export default function PublicShows() {
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "Spettacoli · Areté Teatro";
    api.get("/public/shows").then((r) => setData(r.data)).catch(() => setData({ events: [], error: true }));
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <header className="bg-black">
        <div className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
          <div className="flex items-center gap-2 mb-5"><span className="h-px w-8 bg-primary" /><span className="text-xs tracking-[0.3em] uppercase text-white/70">Scuola di Teatro · Torino</span></div>
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Areté Teatro" className="h-16 w-16 rounded-full bg-white p-1 object-contain shrink-0" />
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-5xl text-white leading-tight">Spettacoli</h1>
              <p className="text-white/70 mt-2 text-base sm:text-lg">Gli spettacoli in programma di Areté Teatro{data?.year ? ` · ${data.year}` : ""}.</p>
            </div>
          </div>
          <Button onClick={share} variant="outline" className="mt-6 min-h-12 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white" data-testid="public-shows-share"><Share2 size={16} className="mr-2" /> Condividi</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="public-shows">
        {!data ? <p className="text-muted-foreground" data-testid="public-shows-loading">Caricamento…</p>
          : data.error ? <p className="text-muted-foreground" data-testid="public-shows-error">Impossibile caricare gli spettacoli. Riprova più tardi.</p>
          : data.events.length === 0 ? <p className="text-muted-foreground text-lg" data-testid="public-shows-empty">Nessuno spettacolo in programma al momento</p>
          : data.events.map((ev) => <PublicEvent key={ev.id} ev={ev} />)}
      </main>
    </div>
  );
}
